<template>
  <div>
    <div
      class="modal-card"
      :class="renewalOptOutModal ? 'do-not-renew-modal' : ''"
    >
      <header
        v-if="title"
        class="modal-card-head"
      >
        <p class="modal-card-title">
          {{ title }}
        </p>
      </header>
      <section class="modal-card-body confirmation-message">
        <templated-markdown
          v-if="message"
          :values="values"
        >
          {{ messageWithParameters }}
        </templated-markdown>
        <ReferAFriend
          v-if="referAFriend"
          :customer-reference="referAFriend.customerReference"
          :default-product="referAFriend.defaultProduct"
        />
        <b-field
          v-if="renewalOptOutModal"
          type="is-info"
        >
          <b-input
            id="updateReasonInput"
            v-model="renewalOptOutReasonValue"
            type="textarea"
            minlength="1"
            maxlength="200"
            placeholder="Please enter your reason here."
            @input="updateReason"
          />
        </b-field>
        <b-field
          v-if="checkBoxText"
          type="is-info"
        >
          <b-checkbox
            id="check-box-input"
            v-model="checkboxValue"
          >
            {{ checkBoxText }}
          </b-checkbox>
        </b-field>
      </section>
      <footer class="modal-card-foot">
        <template v-for="(a, i) in buttons">
          <LinkConfigButton
            v-if="!hideRenewalButtonForPolicyChangeAction(a)"
            :key="i"
            :config="a"
            @click="click(a)"
          />
        </template>
      </footer>
    </div>
    <b-loading :is-full-page="true" />
  </div>
</template>

<script lang="ts">
import type { LinkConfig } from '@/configurations/config-objects/IConfiguration'
import type { ILinkConfigSpecification } from '@/configurations/config-objects/LinkConfigSpecification'
import type ProductEnum from '@/services/enums/ProductEnum'
import type Policy from '@/services/models/Policy'
import { ModalProgrammatic } from 'buefy'
import moment from 'moment'
import {
  Component,
  Prop,
  Vue,
} from 'vue-property-decorator'
import { LinkConfigActionTypes } from '@/configurations/config-objects/LinkConfigSpecification'
import LinkConfigFactory from '@/configurations/LinkConfigFactory'
import Routes from '@/constants/Routes'
import AvailableActionsEnum from '@/services/enums/AvalableActionsEnum'
import usePoliciesStore from '@/stores/policies-store'

@Component({
  components: {
    TemplatedMarkdown: () => import('@/components/controls/TemplatedMarkdown.vue'),
    LinkConfigButton: () => import('./LinkConfigButton.vue'),
    ReferAFriend: () => import('@/components/account/ReferAFriend.vue'),
  },
})
export default class LinkConfigModal extends Vue {
  @Prop() title!: string

  @Prop({ default: '' }) message!: string

  @Prop() referAFriend?: { customerReference: string, defaultProduct: ProductEnum }

  @Prop() checkBoxText?: string

  @Prop() buttons!: LinkConfig[]

  @Prop() isRenewalOptOutModal!: boolean

  @Prop() isThreeYearPriceFixPolicyOptInModal?: boolean

  @Prop() policy!: Policy

  renewalOptOutReasonValue = ''

  checkboxValue = false

  get values() {
    return {
      renewalDate: moment(this.policy?.renewalDate).format('DD/MM/YYYY'),
    }
  }

  get messageWithParameters() {
    let parsedMessage = this.message

    if (this.message.includes('{{ policy.subscriptionCalculatedTotalPayable }}'))
      parsedMessage = parsedMessage.replace('{{ policy.subscriptionCalculatedTotalPayable }}', (this.policy?.subscriptionCalculatedTotalPayable).toFixed(2))

    return parsedMessage
  }

  get renewalOptOutModal() {
    return this.isRenewalOptOutModal
  }

  get threeYearPriceFixPolicyOptInModal() {
    return this.isThreeYearPriceFixPolicyOptInModal === true
  }

  hideRenewalButtonForPolicyChangeAction(button: LinkConfig) {
    return this.title === 'Make Policy Change' && !this.policy.availableActions.find(a => a === AvailableActionsEnum.manageRenewal)
      && button.action.type === LinkConfigActionTypes.routerPush && button.action.url === Routes.renewal
  }

  updateReason() {
    this.$events.emit('updateReason', this.renewalOptOutReasonValue)
  }

  async click(config: LinkConfig) {
    if (config.action.url === 'doNotRenew' && this.renewalOptOutReasonValue === '' && this.isRenewalOptOutModal) {
      const updateReasonElement = this.$el.querySelector('#updateReasonInput')
      const options = { behavior: 'smooth', block: 'center' } as ScrollIntoViewOptions
      updateReasonElement?.scrollIntoView(options)

      return
    }

    if (!this.checkboxValue && this.checkBoxText && config.action.url !== 'close') {
      this.$buefy.toast.open({
        duration: 5000,
        message: 'Please tick the checkbox if you wish to proceed.',
        type: 'is-danger',
        position: 'is-bottom',
      })
      return
    }

    if (this.isThreeYearPriceFixPolicyOptInModal && config.action.url === 'three-year-price-fix-opt-in') {
      const policyNumbers: string[] = usePoliciesStore().policies?.filter(policy => policy.isEligibleFor3YPF).map(policy => policy.policyNumber) ?? [this.policy.policyNumber]

      try {
        await this.$services.policyService.updateThreeYearPriceFixOnPolicies(policyNumbers)

        const modalConfig = {
          title: 'Your Price has been fixed',
          message: 'Your premium will remain fixed for 3 years from the start date of this original agreement, provided no changes are made to your policy, including claims made.\n\nYour total monthly price includes a monthly fee of £6. The fee may be subject to review at any time just like a non-fixed platinum policy. Any increase to this fee would not apply until the annual anniversary of your policy.\n\nThis is not a 3 year contract and you may cancel at any time without being charged a cancellation fee.',
          buttons: [
            {
              name: 'Close',
              action: {
                type: 'redirect',
                url: window.location.href,
              },
            },
          ],
        }

        ModalProgrammatic.open({
          component: LinkConfigModal,
          hasModalCard: true,
          canCancel: ['escape', 'outside'],
          props: {
            title: modalConfig.title,
            message: modalConfig.message,
            buttons: modalConfig.buttons.map(b => LinkConfigFactory.create(b as ILinkConfigSpecification)),
          },
        })
      }
      catch {
        this.$buefy.toast.open({
          duration: 5000,
          message: 'Something has gone wrong, please try again or come back later. If you are still having issues please contact us.',
          type: 'is-danger',
          position: 'is-bottom',
        })
      }
    }

    this.$emit('close')
  }
}
</script>
